import React, { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'

interface SidePanelProps {
  isOpen: boolean
  onClose: () => void
  title: string
  width?: number
  /**
   * z-index. 기본 50. 한 화면에서 두 SidePanel을 동시에 열 때 위에 있어야 하는 패널은
   * 더 높은 값(예: 60), 아래에 있어야 하는 패널은 더 낮은 값(예: 40)으로 지정.
   */
  zIndex?: number
  titleActions?: React.ReactNode
  headerActions?: React.ReactNode
  children: React.ReactNode
}

const SidePanel: React.FC<SidePanelProps> = ({
  isOpen,
  onClose,
  title,
  width = 800,
  zIndex = 50,
  titleActions,
  headerActions,
  children
}) => {
  const panelRef = useRef<HTMLDivElement>(null)

  // Handle escape key and outside clicks
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && panelRef.current && !panelRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement
        // 모달 오버레이 클릭 시 SidePanel이 닫히지 않도록 방지
        if (target.closest('[data-modal-overlay]')) {
          return
        }
        // 다른 SidePanel(예: 목록 위에 띄운 상세 패널) 안쪽 클릭은 외부 클릭으로 간주하지 않음.
        // 이렇게 해야 상세 패널을 조작하는 동안 아래의 목록 패널이 자동으로 닫히지 않음.
        if (target.closest('[data-side-panel]')) {
          return
        }
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      // 약간의 딜레이 후 클릭 리스너 추가 (패널 열기 클릭이 바로 닫히는 것 방지)
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside)
      }, 100)

      return () => {
        document.removeEventListener('keydown', handleEscape)
        document.removeEventListener('mousedown', handleClickOutside)
        clearTimeout(timeoutId)
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div
      ref={panelRef}
      data-side-panel
      className={`fixed top-0 right-0 h-full bg-white shadow-xl transform transition-transform duration-300 ease-in-out flex flex-col ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}
      style={{
        width: `${width}px`,
        zIndex,
        boxShadow: '-4px 0px 20px 0px rgba(0, 0, 0, 0.25)',
        borderLeft: `1px solid ${designTokens.colors.gray[1]}`
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between gap-[10px] px-6 py-0 border-b"
        style={{
          height: '70px',
          borderBottomColor: designTokens.colors.gray[1],
          borderBottomWidth: '1px'
        }}
      >
        <div className="flex items-center gap-2">
          <h2
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['title-small'][0],
              lineHeight: designTokens.typography.fontSize['title-small'][1].lineHeight,
              fontWeight: designTokens.typography.fontSize['title-small'][1].fontWeight,
              color: designTokens.colors.gray[6]
            }}
          >
            {title}
          </h2>
          {titleActions}
        </div>

        <div className="flex items-center gap-[5px]">
          {headerActions}
          <button
            onClick={onClose}
            className="flex items-center justify-center rounded transition-colors cursor-pointer"
            style={{
              width: '38px',
              height: '34px',
              padding: '0px',
              backgroundColor: designTokens.colors.primary.dark,
              border: `2px solid ${designTokens.colors.primary.dark}`,
              borderRadius: '4px',
              color: designTokens.colors.gray[0]
            }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {children}
      </div>
    </div>
  )
}

export default SidePanel